import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import DashboardHeader from '@/components/DashboardHeader';
import StatCard from '@/components/StatCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Mail, 
  FileText, 
  Users, 
  Send, 
  Settings,
  Plus,
  Edit,
  Trash2,
  History
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import SendNewsletterWizard from '@/components/SendNewsletterWizard';

const SendmireinDashboard = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [uniqueSubscribers, setUniqueSubscribers] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (token) {
      checkAccess();
      loadData();
    }
  }, [token]);

  const checkAccess = async () => {
    try {
      const response = await api.myCompany(token);
      setEnabled(response.sendmireinEnabled || false);
    } catch (error) {
      console.error('Error checking Sendmirein access:', error);
      setEnabled(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [subscriberCount, templates] = await Promise.all([
        api.newslettersSubscriberCount(token),
        api.newslettersTemplatesList(token),
      ]);
      
      setUniqueSubscribers(subscriberCount.totalUniqueSubscribers || 0);
      setTemplates(templates);
    } catch (error) {
      console.error('Error loading Sendmirein data:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.dashboard.loadError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm(t('sendmirein.dashboard.confirmDeleteTemplate'))) return;

    try {
      await api.newslettersTemplatesDelete(token, id);
      toast({
        title: t('sendmirein.common.success'),
        description: t('sendmirein.dashboard.templateDeleted'),
      });
      loadData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.dashboard.deleteFailed'),
        variant: 'destructive',
      });
    }
  };

  if (!enabled) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 text-center">
            <Mail className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('sendmirein.unavailable.title')}</h2>
            <p className="text-muted-foreground mb-4">{t('sendmirein.unavailable.desc')}</p>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Mail className="w-8 h-8" />
            {t('nav.newsletterSendmirein')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('sendmirein.dashboard.subtitle')}</p>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title={t('sendmirein.dashboard.potentialRecipients')}
            value={uniqueSubscribers}
            icon={Users}
            color="blue"
          />
          <StatCard
            title={t('sendmirein.dashboard.templates')}
            value={templates.length}
            icon={FileText}
            color="green"
          />
          <div className="bg-background rounded-lg shadow p-6">
            <Button
              onClick={() => setWizardOpen(true)}
              className="w-full h-full"
              size="lg"
              disabled={templates.length === 0}
            >
              <Send className="w-5 h-5 mr-2" />
              {t('sendmirein.dashboard.sendNewsletter')}
            </Button>
            {templates.length === 0 && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {t('sendmirein.dashboard.createTemplateHint')}
              </p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t('sendmirein.dashboard.quickActions')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button
              onClick={() => navigate('/sendmirein/templates/new')}
              className="w-full"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sendmirein.dashboard.newTemplate')}
            </Button>
            <Button
              onClick={() => navigate('/sendmirein/history')}
              className="w-full"
              variant="outline"
            >
              <History className="w-4 h-4 mr-2" />
              {t('sendmirein.dashboard.history')}
            </Button>
            <Button
              onClick={() => navigate('/sendmirein/subscribers')}
              className="w-full"
              variant="outline"
            >
              <Users className="w-4 h-4 mr-2" />
              {t('sendmirein.dashboard.subscribers')}
            </Button>
            <Button
              onClick={() => navigate('/sendmirein/settings')}
              className="w-full"
              variant="outline"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t('sendmirein.dashboard.settings')}
            </Button>
          </div>
        </Card>

        {/* Templates */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{t('sendmirein.dashboard.newsletterTemplates')}</h3>
            <Button
              onClick={() => navigate('/sendmirein/templates/new')}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('sendmirein.common.new')}
            </Button>
          </div>

          {templates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p>{t('sendmirein.dashboard.noTemplates')}</p>
              <p className="text-sm mt-2">{t('sendmirein.dashboard.createFirstTemplate')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">{template.name}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">{template.subject}</p>
                    </div>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      template.status === 'PUBLISHED' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-muted text-foreground'
                    }`}>
                      {template.status === 'PUBLISHED' ? t('sendmirein.common.published') : t('sendmirein.common.draft')}
                    </span>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/sendmirein/templates/${template.id}`)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      {t('sendmirein.common.edit')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Newsletter Send Wizard */}
      {wizardOpen && (
        <SendNewsletterWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          templates={templates}
          onSuccess={() => {
            setWizardOpen(false);
            toast({
              title: t('sendmirein.common.success'),
              description: t('sendmirein.dashboard.sendSuccess'),
            });
          }}
        />
      )}
    </div>
  );
};

export default SendmireinDashboard;
